import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  useCancelJob,
  useDocuments,
  useIngest,
  useJobs,
  useStatusCounts,
  useSubmitJob,
  useTaxonomy,
  type DocumentStat,
  type JobOut,
} from "@/api/queries";
import { checkCan, jobPermission, useMember } from "@/auth/member";
import { GeneratePanel } from "@/components/generate-panel";
import { StageTerm, Tip } from "@/components/pipeline-stepper";
import { RunStateChip } from "@/components/run-state-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Label } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { statusFillClass } from "@/lib/status-tone";
import { cn, formatDate } from "@/lib/utils";

/**
 * DESIGN §14.1 — upload a document, then watch the work.
 *
 * ARCHITECTURE §8 invariant 01 is visible here more than anywhere else: the upload
 * returns a job id in a couple of hundred milliseconds, and everything expensive
 * happens in the worker. The Streamlit version ran the whole pipeline inside the
 * request and left the browser spinning for minutes.
 *
 * The job list polls itself while anything is queued or running, and stops when
 * nothing is — see `useJobs`.
 */
const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

const JOB_KINDS = [
  {
    kind: "pipeline",
    label: "Run pipeline",
    hint: "carry pending chunks through to review",
    what: "Takes everything that is waiting — uploaded pieces and questions part-way through — and carries it through the automatic steps up to the review queue. Safe to run again: finished work is skipped.",
    ai: "AI: every step that still has work (extract, split, enrich, embed, judge unclear duplicates).",
  },
  {
    kind: "rescan",
    label: "Rescan",
    hint: "every pair again under the current config (§9.3)",
    what: "Re-compares questions against each other using the current duplicate thresholds. Use it after changing a threshold, to see what the new setting would catch.",
    ai: "Mostly no AI; a request only for pairs the judge has not seen before.",
  },
  {
    kind: "reembed",
    label: "Re-embed",
    hint: "rows whose embedding_model is not current (§5.6)",
    what: "Recomputes the meaning fingerprint for questions made with an older embedding model, so every fingerprint in the bank can be compared with every other.",
    ai: "AI: one embedding request per batch of questions.",
  },
  {
    kind: "reenrich",
    label: "Re-enrich",
    hint: "rows whose enrichment version or prompt is stale",
    what: "Asks the AI to tag and grade again the questions that were enriched with an older version of the instructions, so the whole bank follows the same rules.",
    ai: "AI: one request per batch of 10 questions.",
  },
  {
    kind: "scan-stale",
    label: "Scan stale",
    hint: "approved → stale by version range (§7.6)",
    what: "Marks an approved question as stale when the technology version it covers has fallen out of the range you support, so a person can look at it again.",
    ai: "No AI — a plain version comparison.",
  },
];

/**
 * One line of "where did this job get to".
 *
 * The cursor is real progress: the worker advances it with each committed batch, so a
 * resumed job says where it actually got to (§10.2).
 *
 * A probe keeps its whole report in `totals` (§14.4) because that is how the result
 * reaches the client. Printed raw it is a paragraph of JSON per row, which buries the
 * pipeline jobs this table exists for — so it gets counted instead, and the report
 * itself is read on the Probe screen.
 */
function progressSummary(job: JobOut): string {
  const totals = job.totals ?? {};
  if (job.kind === "probe") {
    const neighbours = (totals as { neighbours?: unknown }).neighbours;
    return Array.isArray(neighbours) ? `${neighbours.length} neighbours` : "—";
  }
  if (job.kind === "generate") {
    // Raw totals would be the prompt hash and a source_doc name; what a person wants is
    // "how far along, and how many are actually new".
    const t = totals as { inserted?: number; requested?: number; literal_duplicates?: number };
    const parts = [`${t.inserted ?? 0} of ${t.requested ?? "?"} written`];
    if (t.literal_duplicates) parts.push(`${t.literal_duplicates} literal duplicate(s) dropped`);
    return parts.join(" · ");
  }
  if (job.kind === "split") {
    // DESIGN §17.11.5: how many it has looked at, and what came of them — not the raw totals.
    const t = totals as { eligible?: number; examined?: number; proposed?: number; kept?: number };
    if (t.examined === undefined) return "—";
    return `${t.examined}${t.eligible ? ` of ${t.eligible}` : ""} looked at · ${t.proposed ?? 0} proposed · ${t.kept ?? 0} kept`;
  }
  if (Object.keys(totals).length > 0) return JSON.stringify(totals).slice(0, 120);
  return job.cursor ? JSON.stringify(job.cursor) : "—";
}

/** The run a `generate` job is writing into — its `source_doc`, once the worker has named
 * it, which is what the run page is keyed on. Null for every other kind of job. */
function generatedRun(job: JobOut): string | null {
  if (job.kind !== "generate") return null;
  const name = (job.totals as { source_doc?: unknown } | null)?.source_doc;
  return typeof name === "string" ? name : null;
}

/** A run's question-status breakdown as one bar, segment widths proportional to
 * count — the same colours as every `Badge` in the app, just without the text so a
 * whole row of runs can be scanned at once. */
function StackBar({ doc }: { doc: DocumentStat }) {
  if (doc.question_total === 0) {
    return <div className="bg-bank-surface-3 h-[7px] w-40 rounded-sm" />;
  }
  return (
    <div className="flex h-[7px] w-40 overflow-hidden rounded-sm">
      {Object.entries(doc.questions).map(([status, n]) =>
        n > 0 ? (
          <span
            key={status}
            className={statusFillClass(status)}
            style={{ width: `${(n / doc.question_total) * 100}%` }}
            title={`${status}: ${n}`}
          />
        ) : null,
      )}
    </div>
  );
}

export default function Ingest() {
  const [file, setFile] = useState<File | null>(null);
  const [force, setForce] = useState(false);
  const [expectedStack, setExpectedStack] = useState("");
  const [mode, setMode] = useState<"upload" | "generate">("upload");
  const fileInput = useRef<HTMLInputElement>(null);

  const ingest = useIngest();
  const submit = useSubmitJob();
  const cancel = useCancelJob();
  const jobs = useJobs(15);
  const documents = useDocuments();
  const { data: counts } = useStatusCounts();
  const { data: stacks } = useTaxonomy("stack");
  // A Viewer watches runs here but starts none; the bank-wide re-runs are an Admin's alone.
  const me = useMember();
  const canUpload = checkCan(me, "create:ingest_upload") || checkCan(me, "create:ingest");
  const canGenerate = checkCan(me, "create:ingest_generate") || checkCan(me, "create:ingest");
  const canIngest = canUpload || canGenerate;
  const canManageStacks = checkCan(me, "create:taxonomy");
  const runnable = JOB_KINDS.filter(({ kind }) => checkCan(me, jobPermission(kind)));

  useEffect(() => {
    if (canUpload && !canGenerate) {
      setMode("upload");
    } else if (!canUpload && canGenerate) {
      setMode("generate");
    }
  }, [canUpload, canGenerate]);

  function upload() {
    if (!file || !expectedStack) return;
    ingest.mutate(
      { file, force, expectedStack },
      {
        onSuccess: () => {
          setFile(null);
          setForce(false);
          setExpectedStack("");
          if (fileInput.current) fileInput.current.value = "";
        },
      },
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <h1 className="text-base font-semibold tracking-tight">Ingest</h1>

      <ErrorNote error={ingest.error ?? submit.error ?? cancel.error} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel>
          <PanelHeader
            title={
              !canIngest ? "Add questions" : mode === "upload" ? "Upload a document" : "Generate with AI"
            }
            actions={
              canUpload && canGenerate ? (
                <div role="tablist" aria-label="How to add questions" className="flex gap-1.5">
                  {(
                    [
                      ["upload", "Upload file"],
                      ["generate", "Generate with AI"],
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      role="tab"
                      aria-selected={mode === value}
                      size="sm"
                      variant={mode === value ? "primary" : "secondary"}
                      onClick={() => setMode(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              ) : undefined
            }
          />
          {!canIngest ? (
            <div className="p-4">
              <Callout>
                Your role can watch runs but not start them — ingesting is for maintainers and admins.
              </Callout>
            </div>
          ) : mode === "generate" && canGenerate ? (
            <GeneratePanel />
          ) : canUpload ? (
            <div className="space-y-3 p-4">
              <div>
                <Label htmlFor="doc">
                  <code>.docx</code> or <code>.txt</code> — DESIGN §1.1 takes nothing else
                </Label>
                <input
                  id="doc"
                  ref={fileInput}
                  type="file"
                  accept=".docx,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="file:border-bank-border-strong file:bg-bank-surface hover:file:bg-bank-surface-2 text-bank-muted w-full text-sm file:mr-3 file:rounded-md file:border file:px-2.5 file:py-1.5 file:text-sm file:font-medium"
                />
              </div>

              <div>
                <FieldLabel htmlFor="expected-stack">Tech stack (required)</FieldLabel>
                <Combobox
                  id="expected-stack"
                  value={expectedStack}
                  onChange={setExpectedStack}
                  options={(stacks ?? []).map((s) => ({ value: s.path, label: s.path }))}
                />
                <p className="text-bank-muted mt-1 text-xs">
                  Tags every question in this file with this stack, so dedup only ever compares questions
                  within the same stack (DESIGN §19.7 rec #7). Not stack-specific content? Use{" "}
                  <code>general</code>.{" "}
                  {canManageStacks ? (
                    <>
                      Don't see the stack you need? Add it on the{" "}
                      <Link to="/stacks" className="text-bank-accent-ink hover:underline">
                        Stacks
                      </Link>{" "}
                      page — it's usable here right away.
                    </>
                  ) : (
                    "Don't see the stack you need? Contact an administrator to add it to the Stacks vocabulary."
                  )}
                </p>
              </div>

              <label className="text-bank-muted flex items-center gap-2 text-sm">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                Ingest again even if this file name was seen before
              </label>

              <Button
                variant="primary"
                disabled={!file || !expectedStack || ingest.isPending}
                onClick={upload}
              >
                {ingest.isPending ? "Uploading…" : "Ingest and queue the pipeline"}
              </Button>

              {ingest.data && (
                <Callout tone="good">
                  {ingest.data.skipped ? (
                    <>
                      <strong>{ingest.data.source_doc}</strong> was already ingested. Tick the box to ingest
                      it again.
                    </>
                  ) : (
                    <>
                      <strong>{ingest.data.source_doc}</strong>: {ingest.data.chars} chars →{" "}
                      {ingest.data.chunks} chunks.
                      {ingest.data.job ? ` Queued job #${ingest.data.job.job_id}.` : ""}
                    </>
                  )}
                </Callout>
              )}

              <p className="text-bank-muted text-xs">
                Chunking happens in the request because it makes no model call. Everything after it —{" "}
                <StageTerm label="Extracted" text="extract" />, <StageTerm label="Split" text="split" />,{" "}
                <StageTerm label="Enriched" text="enrich" />, <StageTerm label="Embedded" text="embed" />,{" "}
                <StageTerm label="Deduped" text="dedup" />, <StageTerm label="Clustered" text="cluster" /> —
                is a job the worker picks up (§8 invariant 01).
              </p>
            </div>
          ) : null}
        </Panel>

        {runnable.length > 0 && (
          <Panel>
            <PanelHeader title="Run something" />
            <div className="space-y-2 p-4">
              {runnable.map(({ kind, label, hint, what, ai }) => (
                <div key={kind} className="flex items-center gap-3">
                  {/* The popover hangs off the wrapper, not the button, so it still opens when the
                      button is disabled while a submit is in flight. */}
                  <span className="group relative">
                    <Button
                      size="sm"
                      disabled={submit.isPending}
                      aria-describedby={`job-tip-${kind}`}
                      onClick={() => submit.mutate(kind)}
                    >
                      {label}
                    </Button>
                    <Tip id={`job-tip-${kind}`} title={label} what={what} ai={ai} align="start" />
                  </span>
                  <span className="text-bank-muted text-xs">{hint}</span>
                </div>
              ))}
              <p className="text-bank-muted border-bank-border mt-3 border-t pt-3 text-xs">
                These spend model quota. The free tier is 20 requests per day per model (DESIGN §19.5 #7), and
                a job that runs out is requeued with its cursor rather than failed — check{" "}
                <code>qbank status</code> before starting a large one.
              </p>
            </div>
          </Panel>
        )}
      </div>

      <Panel>
        <PanelHeader
          title="Runs"
          actions={
            <span className="text-bank-muted text-xs">
              every document ingested — open one to watch it live, from extraction through review
            </span>
          }
        />
        {(documents.data?.items.length ?? 0) === 0 ? (
          <Empty>Nothing ingested yet.</Empty>
        ) : (
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border text-bank-muted sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>document</th>
                  <th className={TH}>uploaded</th>
                  <th className={cn(TH, "text-right")}>questions</th>
                  <th className={TH}>progress</th>
                  <th className={TH}>state</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {documents.data?.items.map((d) => (
                  <tr
                    key={d.source_doc}
                    className="border-bank-border hover:bg-bank-surface-2 border-t transition-colors"
                  >
                    <td className="max-w-50 truncate px-3 py-2 font-mono text-[11px]" title={d.source_doc}>
                      {d.source_doc}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {formatDate(d.uploaded_at)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {d.question_total}
                    </td>
                    <td className="px-3 py-2">
                      <StackBar doc={d} />
                    </td>
                    <td className="px-3 py-2">
                      <RunStateChip doc={d} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/ingest/runs/${encodeURIComponent(d.source_doc)}`}
                        className="text-bank-accent-ink text-xs font-medium hover:underline"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className="flex min-h-0 flex-1 flex-col">
        <PanelHeader
          title="Jobs"
          actions={
            <span className="text-bank-muted text-xs">
              {counts
                ? `${Object.values(counts.counts).reduce((a, b) => a + b, 0)} questions in the bank`
                : ""}
            </span>
          }
        />
        <div className="min-h-0 flex-1 overflow-auto">
          {(jobs.data?.items.length ?? 0) === 0 ? (
            <Empty>No jobs yet.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border text-bank-muted sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>#</th>
                  <th className={TH}>kind</th>
                  <th className={TH}>status</th>
                  <th className={TH}>started</th>
                  <th className={TH}>progress</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {jobs.data?.items.map((job) => (
                  <tr
                    key={job.id}
                    className="border-bank-border hover:bg-bank-surface-2 border-t align-top transition-colors"
                  >
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">{job.id}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{job.kind}</td>
                    <td className="px-3 py-2">
                      <Badge value={job.status} />
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {formatDate(job.started_at)}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {progressSummary(job)}
                      {job.error && (
                        <span className="text-bank-bad-ink block">{job.error.slice(0, 160)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {generatedRun(job) && (
                        <Link
                          to={`/ingest/runs/${encodeURIComponent(generatedRun(job) ?? "")}`}
                          className="text-bank-accent-ink mr-2 text-xs font-medium hover:underline"
                        >
                          View run →
                        </Link>
                      )}
                      {job.kind === "split" && (
                        <Link
                          to="/review?tab=splits"
                          className="text-bank-accent-ink mr-2 text-xs font-medium hover:underline"
                        >
                          Review proposals →
                        </Link>
                      )}
                      {job.status === "queued" && checkCan(me, jobPermission(job.kind)) && (
                        <Button size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate(job.id)}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </div>
  );
}
